import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { axios, queryClient } from '@apps-common/lib'
import { IMyMonitoringAccountListModel } from '@apps-common/types/portal/my-monitoring'

export const QUERY_KEY_SEARCH_MY_MONITORING_ACCOUNTS_INFINITE = (search?: string) =>
  ['searchMyMonitoringAccountsInfinite', search].filter(Boolean)

export const searchMyMonitoringAccountsInfinite = async (
  skip: number,
  take: number,
  search?: string
): Promise<IMyMonitoringAccountListModel[]> =>
  axios.get(`/MyMonitoringAccountManagement/SearchAccounts?search=${search || ''}&skip=${skip}&take=${take}`).then((res) => res.data?.data)

type Props = {
  enabled: boolean
  search?: string
  take: number
}

export const useSearchMyMonitoringAccountsInfinite = ({ enabled = true, search, take }: Props) => {
  const { isLoading, isFetching, isRefetching, isError, data, refetch, fetchNextPage, hasNextPage } = useInfiniteQuery({
    enabled,
    queryKey: QUERY_KEY_SEARCH_MY_MONITORING_ACCOUNTS_INFINITE(search),
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) => searchMyMonitoringAccountsInfinite(pageParam, take, search),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.length < take) {
        return null
      }

      return allPages?.reduce((acc, curr) => acc + (curr?.length || 0), 0)
    }
  })

  const refetchOverride = async () => {
    await queryClient.cancelQueries({ queryKey: QUERY_KEY_SEARCH_MY_MONITORING_ACCOUNTS_INFINITE(search) })
    queryClient.removeQueries({ queryKey: QUERY_KEY_SEARCH_MY_MONITORING_ACCOUNTS_INFINITE(search) })
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY_SEARCH_MY_MONITORING_ACCOUNTS_INFINITE(search) })
    refetch()
  }

  useEffect(
    () => () => {
      queryClient.removeQueries({ queryKey: QUERY_KEY_SEARCH_MY_MONITORING_ACCOUNTS_INFINITE() })
    },
    []
  )

  return {
    isLoading,
    isFetching,
    isRefetching,
    isError,
    data,
    refetch: refetchOverride,
    fetchNextPage,
    hasNextPage
  }
}
